import { useEffect, useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";

import { colors } from "../../consts";
import { useDropdownOptionsAtTop, useElementSize, useOnClickOutside } from "../../hooks";
import { t } from "../../services";

import {
  FlexContainers,
  IconButton,
  Input,
  SearchInput,
  SVG,
  DropdownOptions
} from "../../components";
import { DropdownInputWrapper } from "./styled";
import { createPortal } from "react-dom";

import { useFloating, offset, flip, autoUpdate } from "@floating-ui/react";
import ChipsInput from "../ChipsInput/ChipsInput";

const DropdownInput = ({
  id,
  title,
  placeholder,
  options,
  selectedOption,
  selectedOptions,
  onSelectOption,
  onOptionsFiltered,
  defaultOptionId,
  showCheckboxes,
  hasError,
  disabled,
  isRequired,
  isSmall,
  warningMsg,
  isSearchable,
  useChips,
  InputComponent,
  viewOnly,
  viewOnlyOptionIds,
  optionsListWidth,
  parentWrapperRef,
  className,
  ...props
}) => {
  const [searchText, setSearchText] = useState("");
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [filteredOptionsState, setFilteredOptions] = useState(options);
  const [activeOption, setActiveOption] = useState(null);

  const filteredOptions = useMemo(
    () =>
      onOptionsFiltered
        ? onOptionsFiltered(filteredOptionsState, searchText)
        : filteredOptionsState,
    [filteredOptionsState, searchText, onOptionsFiltered]
  );

  const optionsRef = useRef();
  const inputRef = useRef();
  const optionsListRef = useRef();

  const { width } = useElementSize(inputRef);
  const portal = document.getElementById("dropdown-portal");

  const showAtTop = useDropdownOptionsAtTop(parentWrapperRef, optionsListRef);

  const floatingProps = useFloating({
    open: optionsVisible,
    placement: "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [flip({ padding: 4 }), offset(4)],
    elements: { reference: optionsRef.current }
  });

  useOnClickOutside(optionsRef, (e) => {
    if (optionsListRef?.current && !optionsListRef.current.contains(e.target)) {
      setOptionsVisible(false);
    }
  });

  useEffect(() => {
    setFilteredOptions(
      options.filter((option) => t(option.value)?.toLowerCase().includes(searchText?.toLowerCase()))
    );
  }, [searchText, options]);

  const handleSelectOption = (id) => {
    if (!selectedOptions) {
      setOptionsVisible(false);
      if (!isSearchable) {
        setSearchText("");
      }
    }

    if (useChips) {
      setSearchText("");
    }

    setActiveOption(null);
    onSelectOption(id);
  };

  useEffect(() => {
    if (isSearchable) {
      setSearchText(selectedOption && selectedOption.value ? selectedOption.value : "");
    }
  }, [selectedOption]);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    const handleButtonClick = (e) => {
      if (filteredOptions.length === 0) return;
      const activeOptionIndex =
        !!activeOption && !!filteredOptions[activeOption] ? activeOption : 0;

      if (e.key === "Enter") {
        handleSelectOption(filteredOptions[activeOptionIndex]?.id);
      }

      if (e.key === "ArrowDown") {
        const nextOption =
          activeOption !== null && activeOptionIndex + 1 < filteredOptions.length
            ? activeOptionIndex + 1
            : 0;
        setActiveOption(nextOption);
      }

      if (e.key === "ArrowUp") {
        const nextOption =
          activeOptionIndex > 0 ? activeOptionIndex - 1 : filteredOptions.length - 1;
        setActiveOption(nextOption);
      }
    };

    const inputElement = inputRef.current;
    inputElement.addEventListener("keydown", handleButtonClick);

    return () => {
      inputElement.removeEventListener("keydown", handleButtonClick);
    };
  }, [inputRef.current, filteredOptions, activeOption, onSelectOption]);

  const selectedValues = !useChips
    ? selectedOptions
      ? defaultOptionId !== undefined && selectedOptions.find((opt) => opt.id === defaultOptionId)
        ? t(selectedOptions.find((opt) => opt.id === defaultOptionId).value)
        : selectedOptions.map((opt) => t(opt.value)).join(", ")
      : selectedOption
      ? t(selectedOption?.value)
      : searchText
    : "";

  return (
    <DropdownInputWrapper
      Container={FlexContainers.FlexColumnStart}
      innerRef={optionsRef}
      width={"100%"}
      height={"100%"}
      position={"relative"}
      disabled={disabled || viewOnly}
      isActive={optionsVisible}
      withoutPointerEvents={!isSearchable}
      optionsListWidth={optionsListWidth}
      {...props}
      className={`${className} ${optionsVisible ? "active" : ""}`}>
      <div onClick={() => setOptionsVisible(!optionsVisible)}>
        {isSearchable && useChips ? (
          <ChipsInput
            id={id}
            title={title}
            innerRef={inputRef}
            value={isSearchable ? searchText : selectedOption?.value || searchText}
            setValue={setSearchText}
            active={optionsVisible}
            setActive={setOptionsVisible}
            placeholder={placeholder}
            disabled={disabled}
            chips={selectedOptions}
            onDelete={(id) => onSelectOption(id)}
            isRequired={isRequired}
            TitleComponent={InputComponent}
            icon={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOptionsVisible((prev) => !prev);
                }}>
                <SVG.DownArrowIcon color={colors.RAISIN_50} flipVert={optionsVisible} />
              </IconButton>
            }
          />
        ) : isSearchable ? (
          <SearchInput
            id={id}
            title={title}
            innerRef={inputRef}
            warningMsg={warningMsg}
            showSearchIcon={false}
            value={isSearchable ? searchText : selectedOption?.value || searchText}
            setValue={(value) => {
              setOptionsVisible(true);
              setSearchText(value);
            }}
            placeholder={placeholder}
            disabled={disabled}
            icon={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOptionsVisible((prev) => !prev);
                }}>
                <SVG.DownArrowIcon color={colors.RAISIN_50} flipVert={optionsVisible} />
              </IconButton>
            }
          />
        ) : (
          <Input
            id={id}
            title={title}
            value={selectedValues}
            placeholder={placeholder}
            hasError={hasError}
            disabled={disabled}
            isReadonly={true}
            isRequired={isRequired}
            isSmall={isSmall}
            warningMsg={warningMsg}
            viewOnly={viewOnly}
            innerRef={inputRef}
            inputIcon={
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setOptionsVisible((prev) => !prev);
                }}>
                <SVG.DownArrowIcon color={colors.RAISIN_50} flipVert={optionsVisible} />
              </IconButton>
            }
            InputComponent={InputComponent}
          />
        )}
      </div>

      {!viewOnly &&
        portal &&
        optionsVisible &&
        createPortal(
          <div
            ref={floatingProps.refs.setFloating}
            style={{ width: `${optionsListWidth ?? width}px`, ...floatingProps.floatingStyles }}>
            <DropdownOptions
              isBlock={true}
              visible={optionsVisible}
              options={
                useChips
                  ? filteredOptions.filter(
                      (x) => !selectedOptions.some((option) => option.id === x.id)
                    )
                  : filteredOptions
              }
              selectedOptions={selectedOptions ? selectedOptions : [selectedOption]}
              activeOptionIndex={activeOption}
              onSelectOption={handleSelectOption}
              searchText={searchText}
              showCheckboxes={showCheckboxes}
              viewOnlyOptionIds={viewOnlyOptionIds}
              ref={optionsListRef}
              showAtTop={showAtTop}
            />
          </div>,
          portal
        )}
    </DropdownInputWrapper>
  );
};
DropdownInput.displayName = "DropdownInput";

DropdownInput.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedOption: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.string.isRequired
  }),
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onSelectOption: PropTypes.func.isRequired,
  defaultOptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showCheckboxes: PropTypes.bool,
  disableSearch: PropTypes.bool,
  hasError: PropTypes.bool,
  disabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  isSmall: PropTypes.bool,
  warningMsg: PropTypes.string,
  isSearchable: PropTypes.bool,
  InputComponent: PropTypes.node,
  viewOnly: PropTypes.bool,
  viewOnlyOptionIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  ),
  optionsListWidth: PropTypes.string,
  parentWrapperRef: PropTypes.object,
  useChips: PropTypes.bool,
  onOptionsFiltered: PropTypes.func,
  className: PropTypes.string
};

export default DropdownInput;
